import React from 'react';
import './RecommendedCourses.css';
import {FALLBACK_IMG} from "../../constants/constants";
import { useTranslation } from 'react-i18next';

export type CourserListItem = {
  id?: string | number;
  image?: string | null;
  title: string;
  lessons: number;
  onClick?: () => void;
};

export type CourserListProps = {
  items: CourserListItem[];
  onItemClick?: (item: CourserListItem, index: number) => void;
  loading?: boolean;
};

export default function CourserList({ items, onItemClick, loading }: CourserListProps) {
  const { t } = useTranslation();
  // When loading, show skeletons instead of empty state
  if (!loading && (!items || items.length === 0)) {
    return <div className="rc-empty">{t('product.courses.noCourses')}</div>;
  }

  const isLoading = !!loading;
  const skeletonCount = isLoading ? Math.max(6, items?.length || 0) : 0;
  const list = isLoading ? Array.from({ length: skeletonCount }) : items;

  return (
    <div className="rc-grid">
      {list!.map((item: any, index: number) => {
        if (isLoading) {
          return (
            <button key={`sk-${index}`} className="rc-card rc-card--skeleton" type="button" disabled aria-busy="true" aria-label={t('common.loading') || 'Loading...'}>
              <div className="rc-card-header">
                <div className="rc-thumb rc-skel rc-skel-rect" />
                <div className="rc-texts">
                  <div className="rc-skel rc-skel-line rc-skel-line--short" />
                  <div className="rc-skel rc-skel-line" />
                </div>
              </div>
            </button>
          );
        }

        const imgSrc = (item.image || FALLBACK_IMG) as string;
        return (
          <button
            key={(item.id ?? index).toString()}
            className="rc-card"
            onClick={() => {
              (item as CourserListItem).onClick?.();
              onItemClick?.(item as CourserListItem, index);
            }}
            type="button"
          >
            <div className="rc-card-header">
              <div className="rc-thumb">
                <img
                  src={imgSrc}
                  alt={(item as CourserListItem).title}
                  onError={(e) => {
                    console.error(e);
                    const target = e.target as HTMLImageElement;
                    target.src = FALLBACK_IMG;
                  }}
                />
              </div>
              <div className="rc-texts">
                <div className="rc-title" title={(item as CourserListItem).title}>{(item as CourserListItem).title}</div>
                <div className="rc-sub">{t('product.courses.lessons')} {(item as CourserListItem).lessons}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
